import React, { Fragment, ReactNode } from "react";
import { LocaleValue } from "../types/cms.types";

/**
* Picks the active language's value from a CMS locale object.
* Falls back to the given text when the CMS field is missing or empty,
* so partially filled documents never blank out the UI.
*
* @param field CMS locale object (may be undefined)
* @param language Active i18next language ("cz" | "en")
* @param fallback Built-in text used when the CMS value is missing
*/
export const pickCmsText = (
    field: LocaleValue | undefined,
    language: string,
    fallback: string
): string => {
    const lang = language.startsWith("en") ? "en" : "cz";
    const value = field?.[lang];
    return value && value.trim() !== "" ? value : fallback;
};

/**
* Renders a CMS text using the editor-friendly markup conventions:
* `*text*` becomes a highlighted <span> and newlines become <br>.
* Mirrors what <Trans> renders for the built-in hero titles.
*/
export const renderHighlight = (text: string): ReactNode => {
    const lines = text.split("\n");
    return lines.map((line, lineIndex) => (
        React.createElement(
            Fragment,
            { key: lineIndex },
            lineIndex > 0 && React.createElement("br"),
            ...line.split(/\*([^*]+)\*/g).map((part, partIndex) =>
                partIndex % 2 === 1
                    ? React.createElement("span", { key: partIndex }, part)
                    : part
            )
        )
    ));
};

/**
* Splits a CMS multi-paragraph text into paragraphs (blank line = break).
*/
export const splitParagraphs = (text: string): string[] =>
    text
        .split(/\n\s*\n/)
        .map((paragraph) => paragraph.trim())
        .filter((paragraph) => paragraph !== "");
